import React from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export const Button = React.forwardRef(({ 
  children, 
  asChild = false,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  className,
  type = 'button',
  style,
  ...props 
}, ref) => {
  const baseClasses = `
    inline-flex items-center justify-center font-medium leading-none transition-colors
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
    disabled:opacity-50 disabled:cursor-not-allowed
    font-body
  `;
  
  // Button variants based on UX Base Design specifications
  const variants = {
    // Primary button: primary background, white text, primary-hover on hover
    primary: `
      bg-primary text-white border-transparent
      hover:bg-primary-hover hover:enabled:bg-primary-hover
      hover:text-[var(--primary-soft,#DBEAFE)] hover:enabled:text-[var(--primary-soft,#DBEAFE)]
      focus:ring-primary
    `,
    
    // Secondary button: white background, text-primary text, border
    secondary: `
      bg-white text-text-primary border border-solid border-border
      hover:bg-surface hover:enabled:bg-surface
      focus:ring-primary
    `,
    
    // Outline button: transparent background, primary text, border
    outline: `
      bg-transparent text-primary border-2 border-solid
      hover:bg-primary hover:text-[var(--primary-soft,#DBEAFE)] hover:enabled:bg-primary hover:enabled:text-[var(--primary-soft,#DBEAFE)]
      focus:ring-primary
    `,
    
    // Ghost button: transparent background, primary text
    ghost: `
      bg-transparent text-primary border-transparent
      hover:bg-surface hover:enabled:bg-surface
      focus:ring-primary
    `,
    
    // Success button
    success: `
      bg-success text-white border-transparent
      hover:bg-emerald-600 hover:enabled:bg-emerald-600
      focus:ring-success
    `,
    
    // Warning button
    warning: `
      bg-warning text-white border-transparent
      hover:bg-amber-600 hover:enabled:bg-amber-600
      focus:ring-warning
    `,
    
    // Danger/Error button
    danger: `
      bg-error text-white border-transparent
      hover:bg-red-600 hover:enabled:bg-red-600
      focus:ring-error
    `
  };

  // Size variants based on UX Base Design spacing system
  const sizes = {
    xs: 'h-7 px-2.5 text-xs rounded-md',       // compact
    sm: 'h-8 px-3 text-sm rounded-md',         // compact small
    md: 'h-9 px-4 text-sm rounded-lg',         // default
    lg: 'h-10 px-5 text-base rounded-lg',      // large
    xl: 'h-11 px-6 text-lg rounded-xl'         // extra large
  };

  const classes = twMerge(clsx(
    baseClasses,
    variants[variant],
    sizes[size],
    className
  ));

  const renderContent = (label) => (
    <>
      {loading && (
        <svg
          className="-ml-1 mr-2 h-4 w-4 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {label}
    </>
  );

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ref,
      style,
      className: twMerge(clsx(children.props?.className, classes)),
      'data-otg-component': props['data-otg-component'] || 'Button',
      'aria-disabled': disabled || loading ? true : undefined,
      children: renderContent(children.props?.children),
    });
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      style={style}
      disabled={disabled || loading}
      {...props}
      data-otg-component={props['data-otg-component'] || 'Button'}
    >
      {renderContent(children)}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
